import { Component, Input } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { finalize } from 'rxjs';
import { formatNumber } from '../../../helpers/formatNumbers';
import {
  ClientTokenUsageResponse,
  Organisation,
  OrganisationService,
} from '../../../services/organisation.service';

@Component({
  selector: 'app-token-usage-dialog',
  templateUrl: './token-usage-dialog.component.html',
  styleUrl: './token-usage-dialog.component.scss',
})
export class TokenUsageDialogComponent {
  @Input() data: Organisation | undefined = undefined;

  usage?: ClientTokenUsageResponse;
  loading = false;
  syncLoading = false;
  period = 'active';

  periods = [
    { value: 'active', label: 'Aktive Periode' },
    { value: 'last_7_days', label: 'Letzte 7 Tage' },
    { value: 'last_30_days', label: 'Letzte 30 Tage' },
    { value: 'current_month', label: 'Kalendermonat' },
  ];

  constructor(
    public dialogRef: NbDialogRef<TokenUsageDialogComponent>,
    protected organisationService: OrganisationService,
    protected nbToastrService: NbToastrService
  ) {}

  ngOnInit(): void {
    this.loadUsage();
  }

  loadUsage() {
    if (!this.data?.id) {
      return;
    }
    this.loading = true;
    this.organisationService
      .getClientTokenUsage(this.data.id, {
        period: this.period,
        breakdown_limit: 100,
      })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((usage) => {
        this.usage = usage;
      });
  }

  scheduleSync() {
    if (!this.data?.id) {
      return;
    }
    const payload = this.usage
      ? {
          start_date: this.usage.period.start_date,
          end_date: this.usage.period.end_date,
        }
      : { days: 2 };

    this.syncLoading = true;
    this.organisationService
      .scheduleClientTokenUsageSync(this.data.id, payload)
      .pipe(finalize(() => (this.syncLoading = false)))
      .subscribe({
        next: () => {
          this.nbToastrService.info(
            'Der Client-Sync wurde fuer die angezeigte Periode gestartet.',
            'Token Usage'
          );
        },
        error: (error: HttpErrorResponse) => {
          const detail =
            typeof error.error?.detail === 'string'
              ? error.error.detail
              : error.message;
          const url = error.url ? ` (${error.url})` : '';
          this.nbToastrService.danger(
            `Sync fehlgeschlagen: ${error.status} ${detail}${url}`,
            'Token Usage'
          );
          console.error('Token usage sync failed', error);
        },
      });
  }

  get dailyChartOptions(): echarts.EChartsOption {
    const daily = this.usage?.daily || [];
    const labels = daily.map((day) => this.shortDateLabel(day.date));

    return {
      color: ['#2f80ed', '#24a148', '#9b51e0', '#f2994a', '#00a3a3', '#6f6f6f', '#eb5757'],
      grid: { left: 54, right: 42, top: 56, bottom: 46 },
      legend: {
        top: 0,
        type: 'scroll',
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: (value: any) =>
          typeof value === 'number' ? this.number(value) : value,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLabel: {
          interval: 0,
          rotate: labels.length > 12 ? 35 : 0,
        },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Tokens / Runs',
        },
        {
          type: 'value',
          name: 'Cost',
          axisLabel: {
            formatter: '${value}',
          },
        },
      ],
      series: [
        {
          name: 'Total Tokens',
          type: 'line',
          smooth: true,
          symbolSize: 7,
          data: daily.map((day) => day.total_tokens),
        },
        {
          name: 'Input',
          type: 'line',
          smooth: true,
          symbolSize: 7,
          data: daily.map((day) => day.input_tokens),
        },
        {
          name: 'Output',
          type: 'line',
          smooth: true,
          symbolSize: 7,
          data: daily.map((day) => day.output_tokens),
        },
        {
          name: 'All Runs',
          type: 'line',
          smooth: true,
          symbolSize: 7,
          data: daily.map((day) => day.total_runs),
        },
        {
          name: 'Model Calls',
          type: 'line',
          smooth: true,
          symbolSize: 7,
          data: daily.map((day) => day.llm_runs),
        },
        {
          name: 'Threads',
          type: 'line',
          smooth: true,
          symbolSize: 7,
          data: daily.map((day) => day.thread_count),
        },
        {
          name: 'Cost',
          type: 'line',
          smooth: true,
          symbolSize: 7,
          yAxisIndex: 1,
          data: daily.map((day) => Number((day.total_cost || 0).toFixed(4))),
        },
      ],
    };
  }

  number(value?: number | null) {
    return formatNumber(value || 0);
  }

  cost(value?: number | null) {
    return `$${(value || 0).toFixed(1)}`;
  }

  activePlanName() {
    return this.data?.plan_name || this.data?.current_plan || 'Kein aktiver Plan';
  }

  tokenInput() {
    return this.usage?.summary.input_tokens || 0;
  }

  tokenOutput() {
    return this.usage?.summary.output_tokens || 0;
  }

  tokenTotal() {
    return this.usage?.summary.total_tokens || 0;
  }

  tokenMetricSource() {
    return 'LangSmith';
  }

  useRuntimeTokenMetrics() {
    return false;
  }

  // --- Live-Zaehler vs. LangSmith -------------------------------------
  // Der Kunde sieht in SEINEM Portal `api_usage.token_usage_count` (der
  // Live-Zaehler, an dem auch die Sperre haengt), diese Uebersicht dagegen
  // die LangSmith-Summe. Solange hier nur eine der beiden Zahlen stand, war
  // eine Abweichung unsichtbar — 2026-09 lief ein Kunde bei 2,67 Mio. echtem
  // Verbrauch in sein 5-Mio.-Limit. Beide Zahlen gehoeren nebeneinander.

  runtimeTokenTotal() {
    return this.usage?.runtime_usage?.total_tokens || 0;
  }

  runtimeTokenLimit() {
    return this.usage?.runtime_usage?.token_quota_limit ?? 0;
  }

  tokenDrift() {
    return this.runtimeTokenTotal() - this.tokenTotal();
  }

  tokenDriftRatio() {
    const actual = this.tokenTotal();
    if (!actual) {
      return 0;
    }
    return this.tokenDrift() / actual;
  }

  tokenDriftLabel() {
    const drift = this.tokenDrift();
    const sign = drift > 0 ? '+' : '';
    const pct = (this.tokenDriftRatio() * 100).toFixed(1);
    return `${sign}${formatNumber(drift)} (${sign}${pct} %)`;
  }

  // 10 % deckt Ingestion-Lag ab; darueber ist es ein Zaehlfehler und keine
  // Verzoegerung. Gleiche Schwelle wie LANGSMITH_TOKEN_USAGE_DRIFT_THRESHOLD
  // im Backend.
  tokenDriftIsSignificant() {
    return Math.abs(this.tokenDriftRatio()) >= 0.1 && Math.abs(this.tokenDrift()) >= 25000;
  }

  date(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
  }

  periodLabel() {
    if (!this.usage) {
      return '-';
    }
    const start = this.shortDateParts(this.usage.period.start_date);
    const end = this.shortDateParts(this.usage.period.end_date);
    if (!start || !end) {
      return `${this.usage.period.start_date} - ${this.usage.period.end_date}`;
    }
    if (start.year !== end.year) {
      return `${start.dayMonth} (${start.year}) - ${end.dayMonth} (${end.year})`;
    }
    return `${start.dayMonth} - ${end.dayMonth} (${end.year})`;
  }

  syncIntervalLabel() {
    const interval = this.usage?.sync.interval;
    if (!interval) {
      return '-';
    }
    return `${interval.type === 'daily' ? 'taeglich' : interval.type}, ${interval.window_hours}h Fenster`;
  }

  private shortDateParts(value?: string | null) {
    if (!value) {
      return null;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) {
      return null;
    }
    return {
      year: match[1],
      dayMonth: `${match[3]}.${match[2]}`,
    };
  }

  private shortDateLabel(value?: string | null) {
    return this.shortDateParts(value)?.dayMonth || value || '-';
  }
}
